/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { message as antdMessage } from 'antd';
import { ArgsProps, TypeOpen } from 'antd/es/message/interface';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { EmptyFragment } from '@/primitives/EmptyFragment';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@/primitives/Icons';
import { WrapKey } from '@/primitives/WrapKey';
import { MessageInstance } from '@/state/mgmtPlaneStore';
import './message.css';

export type UseMessageOptions = {
  namespace?: string;
};

type MessageContextValue = Record<string, { api: MessageInstance; context: ReactNode }>;
const MessageCtx = createContext<MessageContextValue>({});

function changeIcon(openFn: TypeOpen, icon: ReactNode): TypeOpen {
  return (content, duration, onClose) => {
    if (React.isValidElement(content) || typeof content === 'string') {
      const d = typeof duration === 'number' ? duration : undefined;
      return openFn({
        content,
        duration: d,
        icon,
        className: 'toast-message',
      });
    }
    if (typeof content === 'object') {
      const args: ArgsProps = {
        icon,
        ...(content as ArgsProps),
      };
      return openFn(args, duration, onClose);
    }
    throw new Error('Invalid message arguments');
  };
}

function buildApi(antdAPI: ReturnType<typeof antdMessage.useMessage>[0]): MessageInstance {
  return {
    feedback: changeIcon(antdAPI.info, EmptyFragment()),
    info: changeIcon(antdAPI.info, InfoIcon({ on: 'toast' })),
    warning: changeIcon(antdAPI.warning, WarningIcon({ on: 'toast' })),
    success: changeIcon(antdAPI.success, SuccessIcon({ on: 'toast' })),
    error: changeIcon(antdAPI.error, ErrorIcon({ on: 'toast' })),
  };
}

/**
 * Provider that initializes message holders. Call useMessage() in children to access the API.
 * Must be rendered once at the app root (or test wrapper).
 */
export function MessageProvider({ children }: { children: ReactNode }) {
  const [defaultAPI, defaultContext] = antdMessage.useMessage({ prefixCls: 'default-message' });
  const [bottomAPI, bottomContext] = antdMessage.useMessage({ prefixCls: 'bottom-message' });

  const value = useMemo(
    () => ({
      default: {
        api: buildApi(defaultAPI),
        context: WrapKey({ children: defaultContext, key: 'default-wrapper' }),
      },
      bottom: {
        api: buildApi(bottomAPI),
        context: WrapKey({ children: bottomContext, key: 'bottom-wrapper' }),
      },
    }),
    [defaultAPI, defaultContext, bottomAPI, bottomContext],
  );

  return React.createElement(MessageCtx.Provider, { value }, value.default.context, children, value.bottom.context);
}

export function useMessage(options?: UseMessageOptions): [MessageInstance, ReactNode] {
  const namespace = options?.namespace || 'default';
  const ctx = useContext(MessageCtx);
  const entry = ctx[namespace];
  if (!entry) {
    throw new Error(`Message namespace "${namespace}" not found. Wrap your app in <MessageProvider>.`);
  }
  return [entry.api, entry.context];
}
